import { uploadFileDirectly } from '../lib/direct-upload';
import type { ServiceCity } from './service-cities';
import {
  executeGraphqlRequest,
  readCachedGraphqlQuery,
} from '../apollo/graphql-request';

export type ServicePriceMode = 'FIXED' | 'FROM' | 'RANGE';
export type ServicePriceCurrency =
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'AUD'
  | 'CAD'
  | 'CHF'
  | 'CNY'
  | 'JPY'
  | 'PLN'
  | 'UAH';
export type ServicePriceUnit =
  | 'HOUR'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'SESSION'
  | 'ITEM'
  | 'PROJECT'
  | 'CUSTOM';
export type ServiceMediaType = 'IMAGE' | 'VIDEO' | 'MUSIC' | 'FILE';

export interface ServiceMedia {
  uuid: string;
  serviceMediaUuid?: string;
  fileName: string;
  mediaType: ServiceMediaType;
  mimeType: string;
  sortOrder: number;
  alt?: string | null;
  publicUrl?: string | null;
  // A frame of an uploaded video; null until the API has made it.
  posterUrl?: string | null;
}

interface UploadedServiceMediaResponse {
  uuid: string;
  serviceUuid: string;
  mediaUuid: string;
  sortOrder: number;
  alt?: string | null;
  fileName: string;
  mediaType: ServiceMediaType;
  mimeType: string;
  publicUrl?: string | null;
}

interface ServiceMediaPayload {
  uuid: string;
  fileName: string;
  mediaType: ServiceMediaType;
  mimeType: string;
  publicUrl?: string | null;
  posterUrl?: string | null;
  sortOrder?: number;
  alt?: string | null;
}

interface ServiceSummaryPayload extends Omit<ServiceSummary, 'medias'> {
  medias?: ServiceMediaPayload[];
}

export interface ServiceSummary {
  uuid: string;
  slug?: string | null;
  title: string;
  shortDescription?: string | null;
  seoDescription?: string | null;
  description?: string | null;
  priceMode: ServicePriceMode;
  priceMin: number;
  priceCurrency: ServicePriceCurrency;
  priceMax?: number | null;
  priceUnit?: ServicePriceUnit | null;
  priceUnitCustom?: string | null;
  medias?: ServiceMedia[];
  cities?: ServiceCity[];
  followsProfileCities?: boolean;
  profile: {
    uuid: string;
    title: string;
    cities?: ServiceCity[];
  };
}

export interface CreateServiceInput {
  profileUuid: string;
  title: string;
  shortDescription?: string;
  seoDescription?: string;
  description?: string;
  priceMode: ServicePriceMode;
  priceMin: number;
  priceCurrency: ServicePriceCurrency;
  priceMax?: number;
  priceUnit?: ServicePriceUnit;
  priceUnitCustom?: string;
  cities?: ServiceCity[];
}

export interface UpdateServiceInput {
  uuid: string;
  title: string;
  slug?: string | null;
  shortDescription?: string;
  seoDescription?: string;
  description?: string;
  priceMode: ServicePriceMode;
  priceMin: number;
  priceCurrency: ServicePriceCurrency;
  priceMax?: number;
  priceUnit?: ServicePriceUnit;
  priceUnitCustom?: string;
  cities?: ServiceCity[];
  followProfileCities?: true;
}

export interface CreateMediaInput {
  serviceUuid: string;
  mediaType: ServiceMediaType;
  fileName: string;
  mimeType: string;
  s3Key: string;
  publicUrl?: string;
}

export interface UploadServiceMediaInput {
  file: File;
  mediaType?: ServiceMediaType;
  serviceUuid: string;
}

export interface RehostServiceMediaInput {
  mediaType?: ServiceMediaType;
  serviceUuid: string;
  sourceUrl: string;
}

export interface UpdateServiceMediaInput {
  alt?: string | null;
  serviceUuid: string;
  uuid: string;
}

export const SERVICE_SLUG_TAKEN_ERROR_CODE = 'SERVICE_SLUG_TAKEN';

const GRAPHQL_URL =
  process.env.REACT_APP_API_GRAPHQL_URL ?? 'http://localhost:3000/graphql';

function resolveApiBaseUrl() {
  const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL?.trim();
  if (configuredBaseUrl) {
    return configuredBaseUrl.replace(/\/+$/, '');
  }

  return GRAPHQL_URL.replace(/\/graphql\/?$/, '');
}

const API_BASE_URL = resolveApiBaseUrl();

function buildUploadHeaders(accessToken?: string): HeadersInit {
  return {
    Accept: 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };
}

function buildJsonHeaders(accessToken?: string): HeadersInit {
  return {
    ...buildUploadHeaders(accessToken),
    'Content-Type': 'application/json',
  };
}

function extractHttpErrorMessage(responseBody: unknown): string | null {
  if (!responseBody || typeof responseBody !== 'object') {
    return null;
  }

  const bodyWithMessage = responseBody as {
    message?: string | string[];
  };

  if (Array.isArray(bodyWithMessage.message)) {
    const joinedMessage = bodyWithMessage.message
      .map((messageItem) => messageItem.trim())
      .filter(Boolean)
      .join(' ');

    return joinedMessage || null;
  }

  if (typeof bodyWithMessage.message === 'string') {
    const normalizedMessage = bodyWithMessage.message.trim();
    return normalizedMessage || null;
  }

  return null;
}

async function executeServiceRequest<TData>(
  query: string,
  variables: Record<string, unknown>,
  accessToken?: string
): Promise<TData> {
  return executeGraphqlRequest<TData>(query, variables, {
    accessToken,
    requestName: 'Services',
  });
}

function normalizeServiceSummary(service: ServiceSummary): ServiceSummary {
  if (!service.medias) {
    return service;
  }

  return {
    ...service,
    medias: normalizeServiceMedias(service.medias),
  };
}

function normalizeServiceMedia(
  media: ServiceMediaPayload,
  fallbackSortOrder: number,
  fallbackAlt: string | null = null
): ServiceMedia {
  return {
    uuid: media.uuid,
    fileName: media.fileName,
    mediaType: media.mediaType,
    mimeType: media.mimeType,
    publicUrl: media.publicUrl ?? null,
    posterUrl: media.posterUrl ?? null,
    sortOrder: typeof media.sortOrder === 'number' ? media.sortOrder : fallbackSortOrder,
    alt: media.alt ?? fallbackAlt,
  };
}

function normalizeServiceMedias(medias: ServiceMediaPayload[]): ServiceMedia[] {
  return medias.map((media, index) => normalizeServiceMedia(media, index));
}

function normalizeServiceMediaWithFallbacks(
  media: ServiceMediaPayload,
  fallbackSortOrder = 0,
  fallbackAlt: string | null = null
): ServiceMedia {
  return normalizeServiceMedia(media, fallbackSortOrder, fallbackAlt);
}

function normalizeServiceSummaryPayload(service: ServiceSummaryPayload): ServiceSummary {
  const { medias, ...serviceWithoutMedias } = service;

  return {
    ...serviceWithoutMedias,
    ...(medias ? { medias: normalizeServiceMedias(medias) } : {}),
  };
}

function normalizeServiceSummaries(services: ServiceSummary[]): ServiceSummary[] {
  return services.map(normalizeServiceSummary);
}

const SERVICE_SUMMARY_SELECTION_SET = `
          uuid
          slug
          title
          shortDescription
          seoDescription
          description
          priceMode
          priceMin
          priceCurrency
          priceMax
          priceUnit
          priceUnitCustom
          medias {
            uuid
            fileName
            mediaType
            mimeType
            publicUrl
            posterUrl
          }
          profile {
            uuid
            title
          }
`;

const SERVICE_CITIES_SELECTION_SET = `
          cities {
            placeId
            label
          }
          followsProfileCities
`;

const SERVICE_DETAILS_SELECTION_SET = `
          uuid
          slug
          title
          shortDescription
          seoDescription
          description
          priceMode
          priceMin
          priceCurrency
          priceMax
          priceUnit
          priceUnitCustom
${SERVICE_CITIES_SELECTION_SET}
          medias {
            uuid
            fileName
            mediaType
            mimeType
            publicUrl
            posterUrl
          }
          profile {
            uuid
            title
            cities: profileCities {
              placeId
              label
            }
          }
`;

const SERVICES_QUERY = `
      query Services {
        services {
${SERVICE_SUMMARY_SELECTION_SET}
        }
      }
    `;

const PROFILE_SERVICES_QUERY = `
      query ProfileServices($profileUuid: ID!) {
        profileServices(profileUuid: $profileUuid) {
${SERVICE_SUMMARY_SELECTION_SET}
        }
      }
    `;

const SERVICE_QUERY = `
      query Service($uuid: ID!) {
        service(uuid: $uuid) {
${SERVICE_DETAILS_SELECTION_SET}
        }
      }
    `;

export async function fetchServices(accessToken?: string): Promise<ServiceSummary[]> {
  const response = await executeServiceRequest<{ services: ServiceSummary[] }>(
    SERVICES_QUERY,
    {},
    accessToken
  );

  return normalizeServiceSummaries(response.services);
}

export function readCachedServices(): ServiceSummary[] | undefined {
  const cachedData = readCachedGraphqlQuery<{ services: ServiceSummary[] }>(SERVICES_QUERY);

  return cachedData ? normalizeServiceSummaries(cachedData.services) : undefined;
}

export async function fetchProfileServices(
  profileUuid: string,
  accessToken?: string
): Promise<ServiceSummary[]> {
  const response = await executeServiceRequest<{ profileServices: ServiceSummary[] }>(
    PROFILE_SERVICES_QUERY,
    { profileUuid },
    accessToken
  );

  return normalizeServiceSummaries(response.profileServices);
}

export function readCachedProfileServices(
  profileUuid: string
): ServiceSummary[] | undefined {
  const cachedData = readCachedGraphqlQuery<{ profileServices: ServiceSummary[] }>(
    PROFILE_SERVICES_QUERY,
    { profileUuid }
  );

  return cachedData ? normalizeServiceSummaries(cachedData.profileServices) : undefined;
}

export async function fetchServiceByUuid(
  serviceUuid: string,
  accessToken?: string
): Promise<ServiceSummary | null> {
  const response = await executeServiceRequest<{ service: ServiceSummaryPayload | null }>(
    SERVICE_QUERY,
    { uuid: serviceUuid },
    accessToken
  );

  return response.service ? normalizeServiceSummaryPayload(response.service) : null;
}

export function readCachedServiceByUuid(
  serviceUuid: string
): ServiceSummary | null | undefined {
  const cachedData = readCachedGraphqlQuery<{ service: ServiceSummaryPayload | null }>(
    SERVICE_QUERY,
    { uuid: serviceUuid }
  );

  if (!cachedData) {
    return undefined;
  }

  return cachedData.service
    ? normalizeServiceSummaryPayload(cachedData.service)
    : null;
}

export async function createServiceRequest(
  input: CreateServiceInput,
  accessToken?: string
): Promise<ServiceSummary> {
  const response = await executeServiceRequest<{ createService: ServiceSummary }>(
    `
      mutation CreateService($input: CreateServiceInput!) {
        createService(input: $input) {
          uuid
          slug
          title
          shortDescription
          seoDescription
          description
          priceMode
          priceMin
          priceCurrency
          priceMax
          priceUnit
          priceUnitCustom
${SERVICE_CITIES_SELECTION_SET}
          profile {
            uuid
            title
          }
        }
      }
    `,
    { input },
    accessToken
  );

  return normalizeServiceSummary(response.createService);
}

export async function updateServiceRequest(
  input: UpdateServiceInput,
  accessToken?: string
): Promise<ServiceSummary> {
  const response = await executeServiceRequest<{ updateService: ServiceSummary }>(
    `
      mutation UpdateService($input: UpdateServiceInput!) {
        updateService(input: $input) {
          uuid
          slug
          title
          shortDescription
          seoDescription
          description
          priceMode
          priceMin
          priceCurrency
          priceMax
          priceUnit
          priceUnitCustom
${SERVICE_CITIES_SELECTION_SET}
          profile {
            uuid
            title
          }
        }
      }
    `,
    { input },
    accessToken
  );

  return normalizeServiceSummary(response.updateService);
}

export async function deleteServiceRequest(
  serviceUuid: string,
  accessToken?: string
): Promise<ServiceSummary> {
  const response = await executeServiceRequest<{ deleteService: ServiceSummary }>(
    `
      mutation DeleteService($uuid: ID!) {
        deleteService(uuid: $uuid) {
          uuid
          slug
          title
          shortDescription
          seoDescription
          description
          priceMode
          priceMin
          priceCurrency
          priceMax
          priceUnit
          priceUnitCustom
          profile {
            uuid
            title
          }
        }
      }
    `,
    { uuid: serviceUuid },
    accessToken
  );

  return normalizeServiceSummary(response.deleteService);
}

export async function createMediaRequest(
  input: CreateMediaInput,
  accessToken?: string
): Promise<ServiceMedia> {
  const response = await executeServiceRequest<{ createMedia: ServiceMediaPayload }>(
    `
      mutation CreateMedia($input: CreateMediaInput!) {
        createMedia(input: $input) {
          uuid
          fileName
          mediaType
          mimeType
          publicUrl
        }
      }
    `,
    { input },
    accessToken
  );

  return normalizeServiceMediaWithFallbacks(response.createMedia);
}

export async function uploadServiceMediaRequest(
  input: UploadServiceMediaInput,
  accessToken?: string
): Promise<ServiceMedia> {
  const uploadedServiceMedia = await uploadFileDirectly<UploadedServiceMediaResponse>({
    accessToken,
    extraFields: input.mediaType ? { mediaType: input.mediaType } : undefined,
    file: input.file,
    routeUrl: `${API_BASE_URL}/services/${encodeURIComponent(input.serviceUuid)}/media`,
  });

  return {
    uuid: uploadedServiceMedia.mediaUuid,
    serviceMediaUuid: uploadedServiceMedia.uuid,
    fileName: uploadedServiceMedia.fileName,
    mediaType: uploadedServiceMedia.mediaType,
    mimeType: uploadedServiceMedia.mimeType,
    sortOrder: uploadedServiceMedia.sortOrder,
    alt: uploadedServiceMedia.alt ?? null,
    publicUrl: uploadedServiceMedia.publicUrl ?? null,
  };
}

export async function rehostServiceMediaRequest(
  input: RehostServiceMediaInput,
  accessToken?: string
): Promise<ServiceMedia> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/media/rehost`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: buildJsonHeaders(accessToken),
      body: JSON.stringify({
        serviceUuid: input.serviceUuid,
        sourceUrl: input.sourceUrl,
        ...(input.mediaType ? { mediaType: input.mediaType } : {}),
      }),
    });
  } catch {
    throw new Error('Unable to reach the API. Ensure the API server is running.');
  }

  let responseBody: unknown;
  try {
    responseBody = (await response.json()) as unknown;
  } catch {
    responseBody = null;
  }

  if (!response.ok) {
    const errorMessage = extractHttpErrorMessage(responseBody);
    throw new Error(
      errorMessage || `Media rehost request failed with status ${response.status}.`
    );
  }

  if (!responseBody || typeof responseBody !== 'object') {
    throw new Error('Media rehost response did not include data.');
  }

  return responseBody as ServiceMedia;
}

export async function removeServiceMediaRequest(
  mediaUuid: string,
  accessToken?: string
): Promise<boolean> {
  const response = await executeServiceRequest<{ removeMedia: boolean }>(
    `
      mutation RemoveMedia($uuid: ID!) {
        removeMedia(uuid: $uuid)
      }
    `,
    { uuid: mediaUuid },
    accessToken
  );

  return response.removeMedia;
}

export async function updateServiceMediaRequest(
  input: UpdateServiceMediaInput,
  accessToken?: string
): Promise<ServiceMedia> {
  const response = await executeServiceRequest<{ serviceUpdateMedia: ServiceMediaPayload }>(
    `
      mutation ServiceUpdateMedia($input: ServiceUpdateMediaInput!) {
        serviceUpdateMedia(input: $input) {
          uuid
          fileName
          mediaType
          mimeType
          publicUrl
        }
      }
    `,
    {
      input: {
        serviceUuid: input.serviceUuid,
        mediaUuid: input.uuid,
        alt: input.alt ?? null,
      },
    },
    accessToken
  );

  return normalizeServiceMediaWithFallbacks(
    response.serviceUpdateMedia,
    0,
    input.alt ?? null
  );
}

export async function reorderServiceMediasRequest(
  serviceUuid: string,
  mediaUuids: string[],
  accessToken?: string
): Promise<ServiceMedia[]> {
  const response = await executeServiceRequest<{ reorderServiceMedias: ServiceMediaPayload[] }>(
    `
      mutation ReorderServiceMedias($serviceUuid: ID!, $mediaUuids: [ID!]!) {
        reorderServiceMedias: serviceReorderMedia(serviceUuid: $serviceUuid, mediaUuids: $mediaUuids) {
          uuid
          fileName
          mediaType
          mimeType
          publicUrl
        }
      }
    `,
    { serviceUuid, mediaUuids },
    accessToken
  );

  return normalizeServiceMedias(response.reorderServiceMedias);
}
